/**
 * Find picked files whose handle is already dead, BEFORE the upload starts.
 *
 * A File can stop being readable between the pick and the upload: an in-app
 * WebView reclaims it, a cloud placeholder (OneDrive, iCloud) is evicted, a
 * card is pulled. Its PUT then fails with a bare status 0 after six retries.
 * One Firefox-on-Windows order lost 565 of 1,002 photos that way, with every
 * failure reporting `firstFailedFileReadable:false` -- and the customer only
 * learnt it at the end, from a count.
 *
 * Reading one byte tells us now, while the customer can still re-pick them.
 *
 * A slow read is NOT a dead one. iOS fetches an "optimised storage" photo from
 * iCloud before it returns even one byte, which can take seconds. A timeout is
 * therefore "slow" and the file is kept; only a read that actually fails is
 * "dead".
 */

export type HandleStatus = "ok" | "slow" | "dead";

/** How long one single-byte read may take before we call the file slow. */
export const PROBE_TIMEOUT_MS = 1_500;

/**
 * After this many slow reads, stop probing and keep the rest unchecked. A run
 * of timeouts means a cloud-backed library, where probing every photo would
 * keep the customer waiting for no benefit.
 */
export const PROBE_MAX_TIMEOUTS = 8;

const readOneByte = async (file: File): Promise<void> => {
  await file.slice(0, 1).arrayBuffer();
};

export async function probeFileHandles(
  files: File[],
  opts: {
    concurrency?: number;
    timeoutMs?: number;
    maxTimeouts?: number;
    read?: (file: File) => Promise<unknown>;
  } = {},
): Promise<HandleStatus[]> {
  const concurrency = Math.max(1, opts.concurrency ?? 4);
  const timeoutMs = opts.timeoutMs ?? PROBE_TIMEOUT_MS;
  const maxTimeouts = opts.maxTimeouts ?? PROBE_MAX_TIMEOUTS;
  const read = opts.read ?? readOneByte;

  // Unprobed files count as slow: unknown, so kept.
  const results: HandleStatus[] = files.map(() => "slow");
  let next = 0;
  let timeouts = 0;

  const probeOne = async (file: File): Promise<HandleStatus> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<HandleStatus>((resolve) => {
      timer = setTimeout(() => resolve("slow"), timeoutMs);
    });
    const attempt = (async (): Promise<HandleStatus> => {
      try {
        await read(file);
        return "ok";
      } catch {
        return "dead";
      }
    })();
    try {
      return await Promise.race([attempt, timedOut]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  };

  const worker = async () => {
    while (next < files.length && timeouts < maxTimeouts) {
      const index = next;
      next += 1;
      const status = await probeOne(files[index]);
      if (status === "slow") timeouts += 1;
      results[index] = status;
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, worker));
  return results;
}
